import { createClient } from '@supabase/supabase-js'

// -----------------------------------------------------------------------------
//  The one Supabase client for the whole app.
//
//  Both values below are PUBLIC. The anon key ships inside every phone that
//  installs this app and grants nothing by itself — Row Level Security in the
//  database decides what any given signed-in user may see. That is why
//  migrations 003 to 007 spend so much effort on policies: they are the actual
//  security boundary, not this file.
//
//  The service_role key must NEVER appear in this project. It bypasses RLS.
// -----------------------------------------------------------------------------

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Fail loudly at startup rather than mysteriously on the first request. A field
// officer seeing "cannot connect" is useless; a developer seeing this is not.
if (!url || !anonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY.\n' +
      'Local development: copy .env.example to .env.local and fill both in.\n' +
      'Deployment: set them as environment variables in Cloudflare Pages.',
  )
}

export const supabase = createClient(url, anonKey, {
  auth: {
    // The session must survive the phone being locked, the browser being
    // closed, and the app being reopened in a village with no signal. Gate 1
    // asks for 30 days; Supabase refreshes the token whenever there is
    // connectivity, and persistSession is what keeps it across restarts.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
})

// -----------------------------------------------------------------------------
//  A note on error handling that applies everywhere in this app.
//
//  Row Level Security denies a SELECT by returning ZERO ROWS, not an error.
//  It denies an INSERT loudly, but denies an UPDATE SILENTLY — the statement
//  succeeds and affects nothing.
//
//  So "no error" never means "it worked". Every write must check what came
//  back. See BUILD_LOG.md, "Decisions that must not be quietly reversed".
// -----------------------------------------------------------------------------
